import dayjs from 'dayjs';
import {useState} from 'react';
import {NavLink} from 'react-router-dom';
import styled from 'styled-components';
import {useMyAccessRestriction} from '@/api/access-restriction';
import {useRefetchMe} from '@/api/session';
import {Button} from '@/components/Button';
import toast from '@/components/HotToast';
import {Typography} from '@/components/Typography';
import {
  FULL_DATE_FORMAT,
  PREBENEFIT_PLAN_ORDER_NAME,
  TRIAL_DURATION,
} from '@/constants';
import {
  AccessRestrictionType,
  BillingStatusType,
  ProductTier,
  ProductType,
} from '@/enums';
import {useSession} from '@/hooks';
import {paths} from '@/router';
import {
  ColumnFlexBox,
  DashRoundedBox,
  GrayRoundedBox,
  PaddingBox,
} from '@/styled-components';
import {MEDIA_QUERIES, palette} from '@/styles';
import {withComma} from '@/utils';
import Img from './assets/pricing001.png';
import {CancelPlanModal} from './CancelPlanModal';
import {ResumePlanModal} from './ResumePlanModal';
import {Box} from './StyledComponents';

export const CurrentPlan = () => {
  const {user, isPaidUser, isSmartStoreActiveUser} = useSession();
  // 계정 제한 중에도 tier·이용 기간은 그대로 내려오므로 제한 여부는 따로 판정한다. IP 제한은 이용 이력에 반영하지 않는다.
  const {data: restriction} = useMyAccessRestriction(user?.id, isPaidUser);
  const isAccountRestricted = restriction === AccessRestrictionType.Account;

  const [openSuspend, setOpenSuspend] = useState<boolean>(false);
  const [openResume, setOpenResume] = useState<boolean>(false);

  const refetchMe = useRefetchMe();

  const onClickChangeButton = () => {
    if (user?.billing?.status === BillingStatusType.Quit) {
      setOpenResume(true);
    } else {
      setOpenSuspend(true);
    }
  };

  const onChangeStatus = () => {
    refetchMe();

    if (openSuspend) {
      toast.success('해지 신청이 완료되었습니다.');
      setOpenSuspend(false);
    } else {
      toast.success('해지 신청이 취소되었습니다.');
      setOpenResume(false);
    }
  };

  if (!user || !isPaidUser) {
    return (
      <Box>
        <Typography variant="bold14">이용 중인 요금제</Typography>
        <DotBox>
          <Typography variant="bold16" color={palette.gray400}>
            이용 중인 요금제가 없습니다.
          </Typography>
          <NavLink to={{pathname: paths.pricing_root}}>
            <Button size="mediumLarge" color="primary" variant="fill" fullWidth>
              요금제 구독하기
            </Button>
          </NavLink>
        </DotBox>
      </Box>
    );
  }

  const isTrial = user?.tier === ProductTier.Trial;
  // 202606 페스타 1일권(Trial tier)은 기간이 다르므로 실제 날짜 차로 계산
  const trialDays =
    isTrial && user.useBeganAt && user.useEndedAt
      ? dayjs(user.useEndedAt).diff(dayjs(user.useBeganAt), 'day')
      : TRIAL_DURATION;
  const isBenefitPlan =
    user.billing &&
    user.billing.orderName.indexOf(PREBENEFIT_PLAN_ORDER_NAME) >= 0;

  return (
    <Box>
      <Typography variant="bold14">
        이용 중인 {isTrial ? '혜택' : '요금제'}
      </Typography>
      <TicketBox>
        <PaddingBox spacing={[16, 7]}>
          <img src={Img} width={101} alt="이용 중인 요금제" />
        </PaddingBox>
        <TicketContent>
          {isTrial ? (
            <Typography variant="bold18">
              마이프차 지도
              <br />
              {isSmartStoreActiveUser
                ? '스마트상점 이용권'
                : `${trialDays}일 무료 체험`}
            </Typography>
          ) : (
            <div>
              <Typography variant="bold18">
                {user.billing?.orderName.replace(
                  `(${PREBENEFIT_PLAN_ORDER_NAME})`,
                  '',
                )}{' '}
              </Typography>
              {isBenefitPlan && (
                <Typography variant="bold14">
                  ({PREBENEFIT_PLAN_ORDER_NAME})
                </Typography>
              )}
            </div>
          )}
          {!isTrial && (
            <div>
              <Typography variant="bold16" color={palette.gray600}>
                {withComma(user.billing?.totalAmount)}원/월
              </Typography>{' '}
              <Typography variant="medium12" color={palette.gray400}>
                (VAT 포함)
              </Typography>
            </div>
          )}
          {/* 계정 제한 중에는 이용 기간 대신 제한 문구를 보여준다 (프로토타입 기준). */}
          {isAccountRestricted ? (
            <Typography
              variant="medium12"
              color={palette.gray600}
              lineHeight={1.5}
            >
              지금은 이용할 수 없어요. 자세한 내용은 1:1 문의로 남겨 주세요.
            </Typography>
          ) : (
            <Typography variant="medium12" color={palette.gray600}>
              이용 기간 : {dayjs(user.useBeganAt).format(FULL_DATE_FORMAT)}~
              {dayjs(user.useEndedAt).format(FULL_DATE_FORMAT)}
            </Typography>
          )}
        </TicketContent>
      </TicketBox>
      {/* 계정 제한 중에는 정기결제가 해지되고 구독 변경도 막히므로 결제 예정·해지 안내를 숨긴다. */}
      {!isAccountRestricted && (
        <>
          {user.billing?.productType === ProductType.Subscription && (
            <>
              {user.billing?.status === BillingStatusType.Quit ? (
                <Typography variant="medium12" color={palette.gray600}>
                  • {dayjs(user.useEndedAt).format(FULL_DATE_FORMAT)}에 구독이
                  해지될 예정입니다.
                </Typography>
              ) : (
                <Typography variant="medium12" color={palette.gray600}>
                  • {dayjs(user.useEndedAt).format(FULL_DATE_FORMAT)}에{' '}
                  {withComma(user.billing?.nextTotalAmount)}원이 결제 예정입니다.
                </Typography>
              )}
              <Typography
                variant="medium12"
                color={palette.gray600}
                onClick={onClickChangeButton}
                style={{cursor: 'pointer', width: 'fit-content'}}
              >
                <u>
                  {user.billing?.status === BillingStatusType.Quit
                    ? '구독 해지 신청 취소하기'
                    : '구독 해지 신청하기'}
                </u>
              </Typography>
              {openSuspend && (
                <CancelPlanModal
                  onClose={() => setOpenSuspend(false)}
                  onComplete={onChangeStatus}
                />
              )}
              {openResume && (
                <ResumePlanModal
                  billingInfo={user.billing}
                  onClose={() => setOpenResume(false)}
                  onComplete={onChangeStatus}
                />
              )}
            </>
          )}
          {user.billing?.productType === ProductType.OneTime && (
            <Typography variant="medium12" color={palette.gray600} lineHeight={1.5}>
              • {dayjs(user.useEndedAt).format(FULL_DATE_FORMAT)}에 이용 종료
              예정입니다.
              <br />
              • 결제 후 24시간 이내에 기능을 이용하지 않은 경우 환불이 가능합니다.
              (환불은 고객센터를 통해 접수)
              <br />• 다시 이용하고 싶으신가요? 이용 기간 종료 후 다시 결제
              부탁드립니다.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

const DotBox = styled(DashRoundedBox)`
  padding-top: 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TicketBox = styled(GrayRoundedBox)`
  padding: 0;
  display: flex;
  position: relative;
  ${MEDIA_QUERIES.mobile} {
    &::before,
    &::after {
      width: 16px;
      height: 16px;
      content: '';
      position: absolute;
      top: 50%;
      margin-top: -8px;
      background-color: white;
      border-radius: 100%;
    }
    &::before {
      left: -8px;
    }
    &&:after {
      right: -8px;
    }
  }
`;

const TicketContent = styled(ColumnFlexBox)`
  padding: 16px 14px;
  border-left: dashed 1px ${palette.gray200};
  gap: 4px;
`;
